import {
  Field,
  ImageField,
  NextImage,
  withDatasourceCheck,
  useSitecoreContext,
} from "@sitecore-jss/sitecore-jss-nextjs";
import clsx from "clsx";
import { ComponentProps } from "@repo/sitecore-lib/component-props";
import * as Container from "@repo/ui/container";
import { isMobile } from "../../utils";
import { useGetImageStyles } from "../hooks/useGetImageStyles";
import { useGetTextStyles } from "../hooks/useGetTextStyles";
import EditorialParagraph from "../paragraph/editorial-paragraph";
import EditorialVideo from "../video/editorial-video";
import styles from "./editorial-image.module.css";

export type EditorialImageProps = ComponentProps & {
  fields: {
    enableSound?: Field<boolean>;
    enableControls?: Field<boolean>;
    enableAutoPlay?: Field<boolean>;
    heading?: Field<string>;
    text?: Field<string>;
    textColour?: Field<string>;
    mobileImage?: ImageField;
    desktopImage?: ImageField;
    mobileVideo?: Field<string>;
    desktopVideo?: Field<string>;
  };
};

const EditorialImage = (props: EditorialImageProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const isEditing = sitecoreContext?.pageEditing;
  const mobileView = isMobile();

  const imageUrl = mobileView
    ? props.fields.mobileImage || props.fields.desktopImage
    : props.fields.desktopImage || props.fields.mobileImage;

  const videoUrl = mobileView
    ? props.fields.mobileVideo?.value || props.fields.desktopVideo?.value
    : props.fields.desktopVideo?.value || props.fields.mobileVideo?.value;

  if (!isEditing && !imageUrl?.value?.src && !videoUrl) {
    return <></>;
  }

  const stylesArray = props.params.styles?.split(" ");

  const { horizontalImageAlign, imageLayout, imageFullWidth } =
    useGetImageStyles(stylesArray!);
  const { textHorizontalAlign, textVerticalAlign } = useGetTextStyles(
    stylesArray!,
  );

  return (
    <Container.Root size="medium">
      <div
        className={clsx(
          styles.editorialImgContainer,
          imageFullWidth && styles.noPadding,
          styles[horizontalImageAlign as string],
          styles[imageLayout as string],
        )}
      >
        <div className={clsx(styles.imageContainer)}>
          {videoUrl ? (
            <EditorialVideo
              {...props}
              videoUrl={videoUrl}
              enableSound={props.fields.enableSound!}
              enableControls={props.fields.enableControls!}
              enableAutoPlay={props.fields.enableAutoPlay!}
              classname={imageLayout!}
            />
          ) : (
            <NextImage
              field={imageUrl}
              data-testid="jss-image"
              editable
              unoptimized
            />
          )}
        </div>
        <EditorialParagraph
          {...props}
          imageFullWidth={imageFullWidth}
          textColour={props.fields.textColour}
          classnames={[
            textHorizontalAlign!,
            textVerticalAlign!,
            imageLayout!,
            horizontalImageAlign!,
          ]}
        />
      </div>
    </Container.Root>
  );
};

export default withDatasourceCheck()<EditorialImageProps>(EditorialImage);
